import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { z } from "zod"

import { logger } from "@/lib/logger"
import { useNavigationShortcuts } from "@/hooks/useNavigationShortcuts"
import { stringArraySchema } from "@/utils/validation/externalBoundarySchemas"
import { warnOnInvalidExternalData } from "@/utils/validation/externalBoundaryValidation"

const log = logger("PersistedPageNavigation")

const persistedNavigationItemsSchema = z.array(
  z
    .object({
      id: z.string(),
      parentId: z.string().optional(),
    })
    .passthrough(),
)

interface PersistedNavigationItem {
  id: string
  parentId?: string
}

const readPersistedIds = (storageKey: string | undefined): string[] => {
  if (typeof window === "undefined" || !storageKey) {
    return []
  }

  const raw = window.sessionStorage.getItem(storageKey)
  if (!raw) {
    return []
  }

  try {
    const parsed = warnOnInvalidExternalData(
      JSON.parse(raw) as unknown,
      stringArraySchema,
      {
        boundary: "Persisted page navigation IDs",
        log,
        data: { storageKey },
      },
    )
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.filter((item): item is string => typeof item === "string")
  } catch {
    return []
  }
}

const readPersistedNavigationItems = (
  storageKey: string | undefined,
): PersistedNavigationItem[] => {
  if (typeof window === "undefined" || !storageKey) {
    return []
  }

  const raw = window.sessionStorage.getItem(storageKey)
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw) as unknown
    const navigationItems = persistedNavigationItemsSchema.safeParse(parsed)

    if (navigationItems.success) {
      return navigationItems.data
        .filter((item) => item.id.length > 0)
        .map((item) => ({
          id: item.id,
          parentId:
            typeof item.parentId === "string" && item.parentId.length > 0
              ? item.parentId
              : undefined,
        }))
    }

    const ids = warnOnInvalidExternalData(parsed, stringArraySchema, {
      boundary: "Persisted page navigation IDs",
      log,
      data: { storageKey },
    })

    if (!Array.isArray(ids)) {
      return []
    }

    return ids
      .filter((item): item is string => typeof item === "string")
      .map((id) => ({ id }))
  } catch {
    return []
  }
}

export const writePersistedNavigationItems = (
  storageKey: string | undefined,
  items: PersistedNavigationItem[],
) => {
  if (typeof window === "undefined" || !storageKey) {
    return
  }

  window.sessionStorage.setItem(storageKey, JSON.stringify(items))
}

interface UsePersistedCollectionNavigationOptions {
  itemIds: string[]
  storageKey: string | undefined
}

export const usePersistedCollectionNavigation = ({
  itemIds,
  storageKey,
}: UsePersistedCollectionNavigationOptions) => {
  const serializedIds = useMemo(() => JSON.stringify(itemIds), [itemIds])
  // Tracks the key the last write went to, not just the value, so a
  // workspace switch that lands on an identical item list still writes to
  // the newly scoped key instead of being skipped as a no-op duplicate.
  const lastWrittenRef = useRef<{ key: string; value: string } | null>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) {
      return
    }

    const last = lastWrittenRef.current
    if (last?.key === storageKey && last.value === serializedIds) {
      return
    }

    lastWrittenRef.current = { key: storageKey, value: serializedIds }
    window.sessionStorage.setItem(storageKey, serializedIds)
  }, [serializedIds, storageKey])
}

interface UsePersistedCollectionNavigationItemsOptions {
  items: PersistedNavigationItem[]
  storageKey: string | undefined
}

export const usePersistedCollectionNavigationItems = ({
  items,
  storageKey,
}: UsePersistedCollectionNavigationItemsOptions) => {
  const serializedItems = useMemo(() => JSON.stringify(items), [items])
  // Tracks the key the last write went to, not just the value, so a
  // workspace switch that lands on an identical item list still writes to
  // the newly scoped key instead of being skipped as a no-op duplicate.
  const lastWrittenRef = useRef<{ key: string; value: string } | null>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) {
      return
    }

    const last = lastWrittenRef.current
    if (last?.key === storageKey && last.value === serializedItems) {
      return
    }

    lastWrittenRef.current = { key: storageKey, value: serializedItems }
    window.sessionStorage.setItem(storageKey, serializedItems)
  }, [serializedItems, storageKey])
}

interface UsePersistedDetailNavigationOptions {
  buildHref: (itemId: string) => string
  currentItemId: string | null | undefined
  itemLabel: string
  storageKey: string | undefined
  onNavigate: (href: string) => void
}

export const usePersistedDetailNavigation = ({
  buildHref,
  currentItemId,
  itemLabel,
  storageKey,
  onNavigate,
}: UsePersistedDetailNavigationOptions) => {
  const [itemIds, setItemIds] = useState<string[]>(() =>
    readPersistedIds(storageKey),
  )

  useEffect(() => {
    setItemIds(readPersistedIds(storageKey))
  }, [storageKey, currentItemId])

  const currentIndex = useMemo(() => {
    if (!currentItemId) {
      return -1
    }

    return itemIds.indexOf(currentItemId)
  }, [currentItemId, itemIds])

  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < itemIds.length - 1

  const handlePrevious = useCallback(() => {
    if (!hasPrevious) {
      return
    }

    const previousId = itemIds[currentIndex - 1]
    if (!previousId) {
      return
    }

    onNavigate(buildHref(previousId))
  }, [buildHref, currentIndex, hasPrevious, itemIds, onNavigate])

  const handleNext = useCallback(() => {
    if (!hasNext) {
      return
    }

    const nextId = itemIds[currentIndex + 1]
    if (!nextId) {
      return
    }

    onNavigate(buildHref(nextId))
  }, [buildHref, currentIndex, hasNext, itemIds, onNavigate])

  useNavigationShortcuts({
    enabled: itemIds.length > 0 && currentIndex >= 0,
    includeHorizontalArrows: true,
    nextLabel: `Next ${itemLabel}`,
    onNext: hasNext ? handleNext : undefined,
    onPrevious: hasPrevious ? handlePrevious : undefined,
    previousLabel: `Previous ${itemLabel}`,
  })

  return {
    currentIndex: currentIndex >= 0 ? currentIndex + 1 : null,
    handleNext,
    handlePrevious,
    hasNext,
    hasPrevious,
    totalItems: itemIds.length,
  }
}

interface UsePersistedDetailItemNavigationOptions {
  buildHref: (item: PersistedNavigationItem) => string
  currentItemId: string | null | undefined
  fallbackItems?: PersistedNavigationItem[]
  itemLabel: string
  storageKey: string | undefined
  onNavigate: (href: string) => void
}

export const usePersistedDetailItemNavigation = ({
  buildHref,
  currentItemId,
  fallbackItems = [],
  itemLabel,
  storageKey,
  onNavigate,
}: UsePersistedDetailItemNavigationOptions) => {
  const [storedItems, setStoredItems] = useState<PersistedNavigationItem[]>(
    () => readPersistedNavigationItems(storageKey),
  )

  useEffect(() => {
    setStoredItems(readPersistedNavigationItems(storageKey))
  }, [storageKey, currentItemId])

  const itemIds = useMemo(
    () => new Set(storedItems.map((item) => item.id)),
    [storedItems],
  )
  const items =
    currentItemId && itemIds.has(currentItemId) ? storedItems : fallbackItems

  const currentIndex = useMemo(() => {
    if (!currentItemId) {
      return -1
    }

    return items.findIndex((item) => item.id === currentItemId)
  }, [currentItemId, items])

  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < items.length - 1

  const handlePrevious = useCallback(() => {
    if (!hasPrevious) {
      return
    }

    const previousItem = items[currentIndex - 1]
    if (!previousItem) {
      return
    }

    onNavigate(buildHref(previousItem))
  }, [buildHref, currentIndex, hasPrevious, items, onNavigate])

  const handleNext = useCallback(() => {
    if (!hasNext) {
      return
    }

    const nextItem = items[currentIndex + 1]
    if (!nextItem) {
      return
    }

    onNavigate(buildHref(nextItem))
  }, [buildHref, currentIndex, hasNext, items, onNavigate])

  useNavigationShortcuts({
    enabled: items.length > 0 && currentIndex >= 0,
    includeHorizontalArrows: true,
    nextLabel: `Next ${itemLabel}`,
    onNext: hasNext ? handleNext : undefined,
    onPrevious: hasPrevious ? handlePrevious : undefined,
    previousLabel: `Previous ${itemLabel}`,
  })

  return {
    currentIndex: currentIndex >= 0 ? currentIndex + 1 : null,
    currentItem: currentIndex >= 0 ? (items[currentIndex] ?? null) : null,
    handleNext,
    handlePrevious,
    hasNext,
    hasPrevious,
    nextItem: hasNext ? (items[currentIndex + 1] ?? null) : null,
    previousItem: hasPrevious ? (items[currentIndex - 1] ?? null) : null,
    totalItems: items.length,
  }
}
